import React, { useEffect, useState } from "react";
import "../Dashboard/dashboard.css";
import {
  FaProjectDiagram,
  FaClock,
  FaRegCalendarCheck,
  FaFileAlt,
  FaCalendarTimes,
  FaUserCheck,
  FaTasks,
} from "react-icons/fa";
import {
  getReport,
  getAllAssignedProjects,
  getMyLeaves,
  getPendingTasks,
} from "../../service/project_service";
import { toast } from "react-toastify";
import CustomLoadingindicator from "../../components/custom_loading";
import { getProfile, getEmployeeBirthDay } from "../../service/auth_service";
import { useNavigate } from "react-router-dom";
import { getMyAttendanceLogs, getCompanyTomarrowHoliday } from "../../service/attendance_service";

function Dashboard() {
  const navigate = useNavigate();

  const [pendingTasks, setPendingTasks] = useState([]);
  const [tomorrowHoliday, setTomorrowHoliday] = useState(null);
  const [isBirthday, setIsBirthday] = useState(false);

  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalHoursMonth: 0,
    todayHours: 0,
    totalReports: 0,
    totalLeaves: 0,
    pendingLeaves: 0,
    attendanceOfTheMonth: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchDashboardData(), getProfileData(), fetchHolidayData(), fetchBirthdayData()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const fetchBirthdayData = async () => {
    try {
      const response = await getEmployeeBirthDay();
      if (response && response.status === "success" && response.data === true) {
        setIsBirthday(true);
      }
    } catch (error) {
      console.error("Error fetching birthday:", error);
    }
  };

const fetchHolidayData = async () => {
  try {
    const response = await getCompanyTomarrowHoliday();

    if (response?.status === "success" && response.data) {
      setTomorrowHoliday(response.data); 
    } else {
      setTomorrowHoliday(null);
    }
  } catch (error) {
    console.error("Error fetching tomorrow holiday:", error);
    setTomorrowHoliday(null);
  }
};

  const fetchDashboardData = async () => {
    try {
      // Fetch each data source individually to handle partial failures
      let projectsRes = { status: "failed", data: [] };
      let reportsRes = { status: "failed", data: [] };
      let leavesRes = { status: "failed", data: [] };
      let attendanceRes = { status: "failed", data: [] };
      let pendingTasksRes = { status: "failed", data: [] };

      try {
        projectsRes = await getAllAssignedProjects();
      } catch (e) {
        console.error("Projects Fetch Error:", e);
      }
      try {
        reportsRes = await getReport({ startDate: "", endDate: "", projectid: 0 });
      } catch (e) {
        console.error("Reports Fetch Error:", e);
      }
      try {
        leavesRes = await getMyLeaves();
      } catch (e) {
        console.error("Leaves Fetch Error:", e);
      }
      try {
        attendanceRes = await getMyAttendanceLogs();
      } catch (e) {
        console.error("Attendance Fetch Error:", e);
      }
      try {
        pendingTasksRes = await getPendingTasks();
      } catch (e) {
        console.error("Pending Tasks Fetch Error:", e);
      }

      let totalProjects = 0;
      let activeProjects = 0;

      if (projectsRes.status === "success" && projectsRes.data) {
        const projects = projectsRes.data;
        const uniqueProjects = new Set();
        const activeUnique = new Set();

        projects.forEach((p) => {
          if (p.projectid) {
            uniqueProjects.add(p.projectid);
            // If any module is not completed, consider the project active
            const hasActiveModule = p.modules?.some(
              (m) => m.status && m.status.toLowerCase() !== "completed",
            );
            if (hasActiveModule || !p.modules || p.modules.length === 0) {
              activeUnique.add(p.projectid);
            }
          }
        });

        totalProjects = uniqueProjects.size;
        activeProjects = activeUnique.size;
      }

      let totalMinutesMonth = 0;
      let totalMinutesToday = 0;
      let totalReportsCount = 0;
      let reportsList = [];

      if (reportsRes.status === "success" && reportsRes.data) {
        reportsList = reportsRes.data;
        totalReportsCount = reportsList.length;

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Safe "today" string in local YYYY-MM-DD
        const todayStr = now.toISOString().split("T")[0];

        reportsList.forEach((r) => {
          if (!r.workdate || !r.workinghours) return;

          // Parse DATEONLY string robustly
          const parts = r.workdate.split("-");
          const rYear = parseInt(parts[0], 10);
          const rMonth = parseInt(parts[1], 10) - 1;
          const rDay = parseInt(parts[2], 10);

          const val = Number(r.workinghours) || 0;
          const h = Math.floor(val);
          const m = Math.round((val - h) * 100);
          const totalMinutes = h * 60 + m;

          if (rMonth === currentMonth && rYear === currentYear) {
            totalMinutesMonth += totalMinutes;
          }

          if (rYear === currentYear && rMonth === currentMonth && rDay === now.getDate()) {
            totalMinutesToday += totalMinutes;
          }
        });
      }

      let totalLeaves = 0;
      let pendingLeaves = 0;

      // The backend returns an object { summary, leaves: [] }
      const leavesList = leavesRes.data?.leaves || [];

      if (Array.isArray(leavesList)) {
        totalLeaves = leavesList.length;
        pendingLeaves = leavesList.filter(
          (l) => !l.status || l.status === "Pending",
        ).length;
      }

      let attendanceOfTheMonth = 0;
      if (attendanceRes.status === "success" && attendanceRes.data) {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        attendanceOfTheMonth = attendanceRes.data.filter((item) => {
          if (!item.date || !item.status) return false;
          const logDate = new Date(item.date);
          const isCurrentMonth =
            logDate.getMonth() === currentMonth &&
            logDate.getFullYear() === currentYear;

          const isPresent =
            item.status === "Present" ||
            item.status === "Half Day - Morning" ||
            item.status === "Half Day - Afternoon";
          return isCurrentMonth && isPresent;
        }).length;
      }

      if (pendingTasksRes?.status === "success") {
        setPendingTasks(pendingTasksRes.data || []);
      }

      const formatMinToHHmm = (totalMin) => {
        const hours = Math.floor(totalMin / 60);
        const minutes = totalMin % 60;
        return (hours + minutes / 100).toFixed(2);
      };

      setStats({
        totalProjects,
        activeProjects,
        totalHoursMonth: formatMinToHHmm(totalMinutesMonth),
        todayHours: formatMinToHHmm(totalMinutesToday),
        totalReports: totalReportsCount,
        totalLeaves,
        pendingLeaves,
        attendanceOfTheMonth,
      });
    } catch (error) {
      console.error("Dashboard Data Processing Error:", error);
    }
  };

  const getProfileData = async () => {
    try {
      const response = await getProfile();
      if (response.status === "success" && response.data) {
        const data = response.data;
        localStorage.setItem("user_name", data.employeename || "");
        localStorage.setItem(
          "user_profile",
          data.profile ? data.profile : "",
        );
        window.dispatchEvent(new Event("profileUpdated"));
      } else {
        toast.error(response.message || "Failed to fetch profile data");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    }
  };

  if (loading) return <CustomLoadingindicator />;

  const dashData = [
    {
      title: "Attendance",
      value: stats.attendanceOfTheMonth,
      icon: <FaUserCheck />,
      color: "green",
      page: "/attendance",
    },
    {
      title: "Assigned Projects",
      value: stats.totalProjects,
      icon: <FaProjectDiagram />,
      color: "blue",
      page: "/my-projects",
    },
    {
      title: "Active Projects",
      value: stats.activeProjects,
      icon: <FaProjectDiagram />,
      color: "green",
      page: "/my-projects",
    },
    {
      title: "Hours This Month",
      value: stats.totalHoursMonth,
      icon: <FaClock />,
      color: "yellow",
      page: "/my-reports",
    },
    {
      title: "Today's Hours",
      value: stats.todayHours,
      icon: <FaRegCalendarCheck />,
      color: "purple",
      page: "/my-reports",
    },
    {
      title: "Total Reports",
      value: stats.totalReports,
      icon: <FaFileAlt />,
      color: "rose",
      page: "/my-reports",
    },
    {
      title: "Pending Leaves",
      value: stats.pendingLeaves,
      icon: <FaCalendarTimes />,
      color: "orange",
      page: "/leave",
    },
  ];

  return (
    <div className="dashboard-container">
      {isBirthday && (
        <div className="birthday-banner-wrapper">
          <div className="birthday-alert-banner">
            <div className="birthday-alert-icon">🎉</div>
            <div className="birthday-alert-content">
              <h4>Happy Birthday, {localStorage.getItem("user_name")}!</h4>
              <p>Wishing you a fantastic and joyful day from the entire team!</p>
            </div>
            <div className="birthday-confetti">🎊</div>
          </div>
        </div>
      )}
      <div className="dashboard-header">
        <div className="header-text">
          <h2>Welcome {localStorage.getItem("user_name")} 👋</h2>
          <p>Your performance overview for this month</p>
        </div>

        <div className="header-alerts">
          {tomorrowHoliday && (
            <div className="holiday-alert" onClick={() => navigate("/holiday")}>
              <div className="holiday-alert-icon">
                <FaCalendarTimes />
              </div>
              <div className="holiday-alert-content">
                <h4>Tomorrow Holiday</h4>
                <p>{tomorrowHoliday.title}</p>
              </div>
            </div>
          )}

          {pendingTasks.length > 0 && (
            <div className="task-alert" onClick={() => navigate("/tasks")}>
              <div className="task-alert-icon">
                <FaTasks />
              </div>
              <div className="task-alert-content">
                <h4>Pending Tasks</h4>
                <p>{pendingTasks.length} tasks remain</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-grid">
        {dashData.map((item, index) => (
          <DashContainer
            key={index}
            title={item.title}
            value={item.value}
            icon={item.icon}
            color={item.color}
            onClick={() => {
              navigate(item.page);
            }}
          />
        ))}
      </div>
    </div>
  );
}

const DashContainer = ({ title, value, icon, color, onClick }) => {
  return (
    <div className={`dashboard-card card-theme-${color}`} onClick={onClick}>
      <div className={`card-icon`}>{icon}</div>
      <div className="card-content">
        <h4>{title}</h4>
        <h2>{value}</h2>
      </div>
    </div>
  );
};

export default Dashboard;
